import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ClosingCTA, Container, Eyebrow, PageHero, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { Seo } from '../components/seo/Seo';
import { images, principles } from '../data/site';
import { pageSeo } from '../data/seo';
import { breadcrumbSchema, legalServiceSchema, organizationSchema, webPageSchema } from '../utils/schema';
const seo = pageSeo.about;
const crumbs = [{
  label: 'Home',
  path: '/'
}, {
  label: 'About',
  path: '/about'
}];
export function About() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.portrait} schemas={[organizationSchema, legalServiceSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema(crumbs)]} data-id="element-126" data-mp="2s9v8noqafepj" />

      <PageHero eyebrow="About" title="About NexLaw" subtitle="A law firm shaped by the belief that legal services can be more modern, more structured, and more accessible." crumbs={crumbs} data-id="element-127" data-mp="1uhidfb8a0e42" />

      <Container className="pt-14 md:pt-20" data-id="element-128" data-mp="23jj8q2jy8q33">
        <figure className="overflow-hidden rounded-sm bg-sand" data-id="element-129" data-mp="23gdmcv2pg2b2">
          <img src={images.hero} alt="The calm, light-filled modern office environment behind NexLaw" width={1600} height={900} loading="lazy" decoding="async" className="aspect-[3/2] w-full object-cover sm:aspect-[16/9]" data-id="element-130" data-mp="1yilu1zy5ee6c" />
        </figure>
      </Container>

      {/* Why NexLaw exists */}
      <Section ariaLabel="Why NexLaw exists" data-id="element-131" data-mp="2i8fetu2g3t12">
        <div className="grid gap-8 md:grid-cols-12 md:gap-16" data-id="element-132" data-mp="2i9h00rd6dvwi">
          <div className="md:col-span-4" data-id="element-133" data-mp="2w7x8amqorugr">
            <Eyebrow data-id="element-134" data-mp="2w4suqyrbzmdf">Why NexLaw exists</Eyebrow>
            <h2 className="sr-only" data-id="element-135" data-mp="2r93suoru9tdy">Why NexLaw exists</h2>
          </div>
          <div className="md:col-span-8" data-id="element-136" data-mp="1ov6adj7iip3u">
            <p className="font-display text-[26px] font-medium leading-[1.14] tracking-tight text-ink sm:text-[30px] md:text-[40px]" data-id="element-137" data-mp="1ow7nruc03noq">
              Traditional legal services can feel slow, complex, and difficult to
              access.
            </p>
            <p className="mt-7 max-w-prose text-[16.5px] leading-relaxed text-graphite md:mt-8 md:text-[17px]" data-id="element-138" data-mp="22lrmmp2go0sr">
              NexLaw was created to explore a better path — combining legal expertise
              with technology and new service models. The result is a firm that keeps
              professional judgment at the centre while removing the friction that has
              long surrounded it.
            </p>
          </div>
        </div>
      </Section>

      {/* Our approach */}
      <section className="border-y border-line bg-sand/60" data-id="element-139" data-mp="m81cp1onuib7">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-140" data-mp="m9m6uq72bz8j">
          <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-141" data-mp="r62wjotwulnq">
            <div className="md:col-span-5" data-id="element-142" data-mp="jnd53cb21bcp">
              <Eyebrow data-id="element-143" data-mp="joer2btlzi97">Our approach</Eyebrow>
              <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-144" data-mp="jp6egzd1y9oe">
                Four principles we work by.
              </h2>
              <img src={images.portrait} alt="Portrait of a legal professional working in a quiet, daylit office" width={1000} height={1250} loading="lazy" decoding="async" className="mt-10 hidden aspect-[4/5] w-full rounded-sm object-cover md:mt-12 md:block" data-id="element-145" data-mp="9x0ofv6d22bu" />
            </div>
            <ul className="md:col-span-7" data-id="element-146" data-mp="sn9zzvo1f6g7">
              {principles.map((principle, index) => <li key={principle.title} className="border-b border-line py-7 first:border-t md:py-8" data-id="element-147" data-mp="on7wri2nm4yt" data-mpi={index}>
                  <div className="flex items-baseline gap-4 sm:gap-5" data-id="element-148" data-mp="3dbxpcz6za37n">
                    <span className="font-display text-[13px] tracking-[0.14em] text-navy" data-id="element-149" data-mp="39b42m959uugc">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-150" data-mp="15vqzhml10c78">
                      <h3 className="font-display text-[21px] font-medium tracking-tight text-ink sm:text-[23px]" data-id="element-151" data-mp="15w0g4jk6l709">
                        {principle.title}
                      </h3>
                      <p className="mt-2 max-w-prose text-[15.5px] leading-relaxed text-graphite md:text-[16px]" data-id="element-152" data-mp="w5nyp07nxtyc">
                        {principle.body}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* Future of legal services */}
      <Section ariaLabel="The future of legal services" data-id="element-153" data-mp="1euuhsocdcnko">
        <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-16" data-id="element-154" data-mp="1exgqirazx94h">
          <figure className="md:col-span-5" data-id="element-155" data-mp="3vigbfl0lpqew">
            <img src={images.architecture} alt="Repeating architectural geometry in warm daylight, suggesting structure and order" width={1000} height={1250} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover md:aspect-[4/5]" data-id="element-156" data-mp="3fyxalr1jrw7" />
          </figure>
          <div className="md:col-span-7" data-id="element-157" data-mp="vsp21ve9f9u">
            <Eyebrow data-id="element-158" data-mp="x3wcbo1t55p">The future of legal services</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-159" data-mp="39bcgqwgf2pms">
              The future of legal services.
            </h2>
            <p className="mt-6 font-serif text-[21px] font-light leading-[1.36] text-ink sm:text-[24px] md:text-[30px]" data-id="element-160" data-mp="34dkep9m25n4v">
              NexLaw represents a new direction in legal services — one that embraces
              technology while keeping legal judgment, trust, and responsibility at the
              centre.
            </p>
            <div className="mt-8" data-id="element-161" data-mp="2qj9yjgo4n1af">
              <Link to="/contact" className="inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-162" data-mp="2lm9pcwvzdif3">
                Get in touch with NexLaw
                <ArrowForwardIcon sx={{
                fontSize: 16
              }} data-id="element-163" data-mp="3f9e7378guhgm" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <ClosingCTA headline="Want to know more about NexLaw?" body="We are always glad to talk with people curious about a more modern legal model." data-id="element-164" data-mp="3p1tp24jrj61i" />
    </>;
}